// Nexo — instância ÚNICA do mecanismo de ajuda flutuante (canto inferior direito) nos três ambientes
// (admin, cliente, entregador). Montado uma vez no AppShell, FORA do keep-alive de abas (não duplica por
// aba). A ajuda contextual acompanha a aba/rota ATIVA (via location.pathname). Carrega artigos sob demanda
// (sem polling). O painel abre SOBRE a página, sem navegar para fora — exceto a ação "Abrir Central".
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft, BookOpen, Compass, Loader2, Minus, Search, UserCog, X,
} from "lucide-react";
import { useApp } from "@/store/useApp";
import { useHelpPanel } from "@/store/useHelpPanel";
import {
  fetchHelpArticle, fetchHelpForScreen, fetchHelpSections, searchHelp,
  type HelpArticleDetail, type HelpArticleSummary,
} from "@/lib/helpApi";
import { HelpArticleBody } from "@/components/help/HelpArticleBody";
import { NexoMascot } from "./NexoMascot";
import { cn } from "@/lib/utils";

const HIDDEN_PREFIXES = [
  "/login", "/signup", "/onboarding", "/auth", "/redefinir-senha", "/recuperar-acesso",
  "/privacy", "/terms", "/selecao", "/sem-acesso",
];

// Rotas onde o LANÇADOR flutuante (botão do mascote no canto) não aparece porque o Nexo já é o centro da
// própria tela (ex.: boas-vindas) — evita um SEGUNDO mascote. O painel de ajuda ainda pode ser aberto por
// ação da página (ex.: "Preciso de ajuda"): quando `open`, o painel é renderizado normalmente.
const LAUNCHER_HIDDEN_PREFIXES = ["/admin/boas-vindas"];

function centralRouteFor(role: string | undefined): string {
  if (role === "CLIENT") return "/ajuda";
  if (role === "ENTREGADOR") return "/entregador/ajuda";
  return "/admin/ajuda";
}

export function NexoHelp() {
  const account = useApp((s) => s.account);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    open, minimized, reduceMotion, view, articleRef,
    openMenu, openArticle, close, setMinimized, setView,
  } = useHelpPanel();

  const shouldHide = !account || HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));

  const rootRef = useRef<HTMLDivElement>(null);
  // Listener NATIVO no próprio nó do Nexo: interrompe a propagação do pointer/mouse/touch ANTES de chegar ao
  // document, onde o Radix (DismissableLayer) escuta para "fechar ao clicar fora". Assim, clicar no Nexo com
  // um modal aberto NÃO fecha o modal nem descarta o formulário — e os cliques internos do Nexo seguem
  // funcionando (o evento de clique não é bloqueado). Independe de detalhes de bubbling do React.
  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    const stop = (e: Event) => e.stopPropagation();
    // pointer/mouse/touch cobrem o "fechar ao clicar fora"; focusin cobre o "fechar ao focar fora" do Radix.
    node.addEventListener("pointerdown", stop);
    node.addEventListener("mousedown", stop);
    node.addEventListener("touchstart", stop, { passive: true });
    node.addEventListener("focusin", stop);
    // wheel/touchmove: o react-remove-scroll do modal escuta no document (bubble) e dá preventDefault no
    // scroll fora do modal. Interromper a propagação ANTES do document libera a rolagem DENTRO do painel do
    // Nexo (o scroll nativo do container ainda ocorre), sem destravar a rolagem da página de fundo.
    node.addEventListener("wheel", stop, { passive: true });
    node.addEventListener("touchmove", stop, { passive: true });
    return () => {
      node.removeEventListener("pointerdown", stop);
      node.removeEventListener("mousedown", stop);
      node.removeEventListener("touchstart", stop);
      node.removeEventListener("focusin", stop);
      node.removeEventListener("wheel", stop);
      node.removeEventListener("touchmove", stop);
    };
  }, [shouldHide]);

  if (shouldHide) return null;

  // Na tela de boas-vindas o mascote já é o centro: não renderiza o lançador flutuante. O painel ainda pode
  // ser aberto pela própria página ("Preciso de ajuda" → openMenu); só então o painel aparece.
  const hideLauncher = LAUNCHER_HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));
  if (!open && hideLauncher) return null;

  const centralRoute = centralRouteFor(account.role);

  // Portal para o document.body: como o overlay do modal (Radix) também vive num portal no body, só sendo
  // SIBLING do overlay o z-index do Nexo (acima) vence o teste de clique (elementFromPoint). Dentro do #root
  // o z-index não ultrapassa o portal do modal.
  return createPortal(
    <div
      ref={rootRef}
      className="fixed bottom-4 right-4 print:hidden"
      // pointerEvents:auto é ESSENCIAL: um modal Radix aberto aplica pointer-events:none no <body> (deixa o
      // fundo inerte), e o Nexo — filho do body — herdaria "none" e viraria não-clicável (o clique
      // atravessaria para o overlay, que então fecha o modal). Forçar "auto" devolve o hit-test ao Nexo.
      style={{ zIndex: 1000, pointerEvents: "auto", paddingBottom: "env(safe-area-inset-bottom,0px)" }}
    >
      {open ? (
        <HelpPanelCard
          key="panel"
          screenKey={location.pathname}
          view={view}
          articleRef={articleRef}
          reduceMotion={reduceMotion}
          onSetView={setView}
          onOpenArticle={openArticle}
          onNavigate={(path) => navigate(path)}
          onOpenCentral={() => {
            close();
            navigate(centralRoute);
          }}
          onMinimize={() => setMinimized(true)}
          onClose={close}
        />
      ) : minimized ? (
        <button
          type="button"
          onClick={() => setMinimized(false)}
          aria-label="Mostrar ajuda do Nexo"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background/90 text-primary shadow-md backdrop-blur hover:bg-background"
        >
          <BookOpen className="h-4 w-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={openMenu}
          aria-label="Abrir ajuda do Nexo"
          className="group flex h-14 w-14 items-center justify-center rounded-full border border-border bg-background shadow-lg transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <NexoMascot animation="idle" reduceMotion={reduceMotion} size={44} />
        </button>
      )}
    </div>,
    document.body,
  );
}

function HelpPanelCard({
  screenKey, view, articleRef, reduceMotion, onSetView, onOpenArticle, onNavigate, onOpenCentral, onMinimize, onClose,
}: {
  screenKey: string;
  view: ReturnType<typeof useHelpPanel.getState>["view"];
  articleRef: string | null;
  reduceMotion: boolean;
  onSetView: (v: "menu" | "screen" | "roles" | "article") => void;
  onOpenArticle: (slug: string) => void;
  onNavigate: (path: string) => void;
  onOpenCentral: () => void;
  onMinimize: () => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<HelpArticleSummary[]>([]);
  const [searching, setSearching] = useState(false);
  const [article, setArticle] = useState<HelpArticleDetail | null>(null);
  const [roleArticles, setRoleArticles] = useState<HelpArticleSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const debounce = useRef<number | null>(null);

  // Busca (debounce 350ms; só artigos publicados visíveis ao usuário).
  useEffect(() => {
    if (debounce.current) window.clearTimeout(debounce.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounce.current = window.setTimeout(() => {
      searchHelp(q)
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => {
      if (debounce.current) window.clearTimeout(debounce.current);
    };
  }, [query]);

  // Carga sob demanda por view.
  useEffect(() => {
    let cancelled = false;
    setNotice(null);
    if (view === "article" && articleRef) {
      setLoading(true);
      fetchHelpArticle(articleRef)
        .then((a) => { if (!cancelled) setArticle(a); })
        .catch(() => { if (!cancelled) { setArticle(null); setNotice("Artigo indisponível ou sem acesso."); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else if (view === "screen") {
      setLoading(true);
      fetchHelpForScreen(screenKey)
        .then((a) => {
          if (cancelled) return;
          if (a) {
            setArticle(a);
          } else {
            setArticle(null);
            setNotice("Não há um artigo específico para esta tela. Use a busca ou a Central de Ajuda.");
          }
        })
        .catch(() => { if (!cancelled) { setArticle(null); setNotice("Não foi possível carregar a ajuda desta tela."); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else if (view === "roles") {
      setLoading(true);
      fetchHelpSections()
        .then((s) => { if (!cancelled) setRoleArticles(s.minhaFuncao); })
        .catch(() => { if (!cancelled) setRoleArticles([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }
    return () => { cancelled = true; };
  }, [view, articleRef, screenKey]);

  const showArticle = (view === "article" || view === "screen") && article;

  return (
    <div
      role="dialog"
      aria-label="Ajuda do Nexo"
      className="flex max-h-[min(78vh,620px)] w-[min(92vw,380px)] flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl"
    >
      <header className="flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-2">
        <NexoMascot animation="wave" reduceMotion={reduceMotion} size={32} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight">Nexo · Ajuda</p>
          <p className="truncate text-xs text-muted-foreground">Como posso ajudar?</p>
        </div>
        <button type="button" onClick={onMinimize} aria-label="Minimizar"
          className="rounded-md p-1 text-muted-foreground hover:bg-background hover:text-foreground">
          <Minus className="h-4 w-4" />
        </button>
        <button type="button" onClick={onClose} aria-label="Fechar"
          className="rounded-md p-1 text-muted-foreground hover:bg-background hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="relative border-b border-border px-3 py-2">
        <Search className="pointer-events-none absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Pesquisar uma dúvida, rotina ou erro"
          className="w-full rounded-lg border border-border bg-background py-1.5 pl-8 pr-2 text-sm outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {query.trim().length >= 2 ? (
          <SearchResults results={results} searching={searching} onPick={onOpenArticle} />
        ) : showArticle ? (
          <div className="space-y-3">
            <button type="button" onClick={() => onSetView("menu")}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-3.5 w-3.5" /> Voltar
            </button>
            <HelpArticleBody article={article!} onNavigate={onNavigate} onOpenArticle={onOpenArticle} />
          </div>
        ) : view === "roles" ? (
          <RoleRoutines loading={loading} articles={roleArticles} onPick={onOpenArticle} onBack={() => onSetView("menu")} />
        ) : loading ? (
          <Loading />
        ) : notice ? (
          <Notice text={notice} onBack={() => onSetView("menu")} />
        ) : (
          <MenuActions
            onScreenHelp={() => onSetView("screen")}
            onRoles={() => onSetView("roles")}
            onCentral={onOpenCentral}
          />
        )}
      </div>
    </div>
  );
}

function MenuActions({ onScreenHelp, onRoles, onCentral }: {
  onScreenHelp: () => void; onRoles: () => void; onCentral: () => void;
}) {
  const item = "flex w-full items-center gap-3 rounded-lg border border-border px-3 py-2.5 text-left text-sm hover:bg-muted/60";
  return (
    <div className="space-y-2">
      <p className="px-1 text-xs text-muted-foreground">Comece por uma destas opções:</p>
      <button type="button" onClick={onScreenHelp} className={item}>
        <Compass className="h-4 w-4 shrink-0 text-primary" />
        <span><span className="font-medium">Ajuda desta tela</span><br />
          <span className="text-xs text-muted-foreground">Orientação sobre onde você está agora</span></span>
      </button>
      <button type="button" onClick={onRoles} className={item}>
        <UserCog className="h-4 w-4 shrink-0 text-primary" />
        <span><span className="font-medium">Rotinas da minha função</span><br />
          <span className="text-xs text-muted-foreground">O que o seu acesso permite fazer</span></span>
      </button>
      <button type="button" onClick={onCentral} className={item}>
        <BookOpen className="h-4 w-4 shrink-0 text-primary" />
        <span><span className="font-medium">Abrir Central de Ajuda</span><br />
          <span className="text-xs text-muted-foreground">Todos os artigos do seu ambiente</span></span>
      </button>
    </div>
  );
}

function SearchResults({ results, searching, onPick }: {
  results: HelpArticleSummary[]; searching: boolean; onPick: (slug: string) => void;
}) {
  if (searching) return <Loading />;
  if (results.length === 0) {
    return <p className="px-1 py-6 text-center text-sm text-muted-foreground">Nenhum artigo encontrado para a busca.</p>;
  }
  return (
    <ul className="space-y-1.5">
      {results.map((a) => (
        <li key={a.id}>
          <button type="button" onClick={() => onPick(a.slug)}
            className="w-full rounded-lg border border-border px-3 py-2 text-left hover:bg-muted/60">
            <p className="text-sm font-medium leading-tight">{a.title}</p>
            {a.summary ? <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{a.summary}</p> : null}
          </button>
        </li>
      ))}
    </ul>
  );
}

function RoleRoutines({ loading, articles, onPick, onBack }: {
  loading: boolean; articles: HelpArticleSummary[]; onPick: (slug: string) => void; onBack: () => void;
}) {
  return (
    <div className="space-y-3">
      <button type="button" onClick={onBack}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3.5 w-3.5" /> Voltar
      </button>
      {loading ? <Loading /> : articles.length === 0 ? (
        <p className="px-1 py-6 text-center text-sm text-muted-foreground">
          Ainda não há rotinas específicas para a sua função.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {articles.map((a) => (
            <li key={a.id}>
              <button type="button" onClick={() => onPick(a.slug)}
                className="w-full rounded-lg border border-border px-3 py-2 text-left hover:bg-muted/60">
                <p className="text-sm font-medium leading-tight">{a.title}</p>
                {a.summary ? <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{a.summary}</p> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Notice({ text, onBack }: { text: string; onBack: () => void }) {
  return (
    <div className="space-y-3">
      <button type="button" onClick={onBack}
        className={cn("inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground")}>
        <ArrowLeft className="h-3.5 w-3.5" /> Voltar
      </button>
      <p className="rounded-lg border border-border bg-muted/30 px-3 py-4 text-center text-sm text-muted-foreground">
        {text}
      </p>
    </div>
  );
}

function Loading() {
  return (
    <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
    </div>
  );
}
